import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Lock, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const redirect = location.state?.from?.pathname || "/admin";

    React.useEffect(() => {
        if (user) navigate(redirect, { replace: true });
    }, [user, navigate, redirect]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate(redirect, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-caireen-cream px-6 relative overflow-hidden">
            <div aria-hidden className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-caireen-yellow/30 blur-3xl animate-blob" />
            <div aria-hidden className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-caireen-orange/25 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md bg-white rounded-3xl ring-1 ring-caireen-ink/10 shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] p-8 md:p-10"
            >
                <div className="flex items-center gap-3 mb-7">
                    <span className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-caireen-orange/30">
                        <img src="/caireen-logo.jpg" alt="Caireen" className="w-full h-full object-cover" />
                    </span>
                    <div className="leading-tight">
                        <p className="font-serif text-xl">Caireen Verein</p>
                        <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-caireen-ink/60">Admin Console</p>
                    </div>
                </div>

                <h1 className="font-serif text-3xl tracking-tight">Welcome back</h1>
                <p className="mt-1.5 font-sans text-sm text-caireen-ink/60">
                    Sign in to manage submissions, members and donations.
                </p>

                <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="admin-login-form">
                    <div className="space-y-2">
                        <Label className="font-sans text-sm">Email</Label>
                        <div className="relative">
                            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-caireen-ink/40" />
                            <Input
                                data-testid="admin-login-email"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@caireen-verein.at"
                                className="pl-11 rounded-xl bg-caireen-cream/60 border-caireen-ink/15 h-12"
                                autoComplete="username"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label className="font-sans text-sm">Password</Label>
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-caireen-ink/40" />
                            <Input
                                data-testid="admin-login-password"
                                required
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="pl-11 rounded-xl bg-caireen-cream/60 border-caireen-ink/15 h-12"
                                autoComplete="current-password"
                            />
                        </div>
                    </div>

                    {error && (
                        <div data-testid="admin-login-error" className="rounded-xl bg-caireen-red/10 text-caireen-red px-4 py-3 font-sans text-sm">
                            {error}
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="w-full rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold py-6 text-base"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <LogIn className="w-4 h-4 mr-2" />
                                Sign in
                            </>
                        )}
                    </Button>

                    <p className="font-sans text-xs text-caireen-ink/50 inline-flex items-center gap-1.5 justify-center w-full">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Protected · 5 attempts max
                    </p>
                </form>
            </motion.div>
        </div>
    );
}
